/**
 * ポテンシャル分析設定の行数見積もり（FKV3-13111 / FKV3-13122）
 *
 * 設定する側が「階層を浅くする」判断をできるよう、保存前にグループ数と行数を示す。
 * いずれもマスタ全体を母数にした上限で、実際の行数はSKUが属する組み合わせにしか
 * 現れないためこれより小さくなる。
 */

import type { ProductGroupLevelCounts } from '@/lib/types';

/** 推定行数の前提となる週数。事前集計の保持期間734日はおよそ2年 */
export const WEEKS_PER_RETENTION = 104;

/**
 * 推定行数の警告閾値。
 *
 * abdi（12タイプ・最下層10,138グループ）の層2は全期間で約1億行あり、毎日作り直す規模として重い。
 * その1/3を目安に置き、超えたら階層を浅くする判断を促す。上限は設けない（設計 D10）。
 */
export const ESTIMATED_ROWS_WARN_THRESHOLD = 30_000_000;

/**
 * 選んだ階層でのグループ数。グループ数が取れていなければ null。
 *
 * 階層は常に 1〜5 の数値で、最下層は 5（DB が NULL を持たない）。
 * 階層が5に満たないタイプに5を指定すると、そのタイプの最下層までのグループ数になる。
 */
export function groupCountOf(
  counts: ProductGroupLevelCounts | undefined,
  productGroupLevel: number,
): number | null {
  if (!counts) return null;
  return counts.depth_counts[productGroupLevel - 1] ?? 0;
}

/**
 * 行数の見積もり。
 *
 * 層2は「週 × 店舗 × グループ」が1行で、週ごとに全社サマリ行も持つため店舗数に1を足す。
 */
export function estimateRows(groups: number, sellerCount: number): number {
  return groups * (sellerCount + 1) * WEEKS_PER_RETENTION;
}

/** 掛け合わせ1軸ぶんの指定（画面が編集中の形） */
export interface CrossAxisDraft {
  /** 未選択は null */
  productGroupTypeId: string | null;
  /** 出力する階層（1〜5）。最下層は 5 */
  productGroupLevel: number;
}

/**
 * 掛け合わせの組み合わせ数（各軸のグループ数の積）。
 *
 * 軸が1つも選ばれていなければ 0、グループ数が取れない軸があれば null（見積もり不能）。
 */
export function crossCombinations(
  axes: CrossAxisDraft[],
  levelCounts: ProductGroupLevelCounts[] | null,
): number | null {
  if (levelCounts === null) return null;
  const selected = axes.filter((a) => a.productGroupTypeId !== null);
  if (selected.length === 0) return 0;

  let total = 1;
  for (const axis of selected) {
    const count = groupCountOf(
      levelCounts.find((c) => c.type_id === axis.productGroupTypeId),
      axis.productGroupLevel,
    );
    // 1軸でも数が分からなければ過小な見積もりになるため、見積もり自体をやめる
    if (count === null) return null;
    // グループ0のタイプで積が0になると「掛け合わせても行が出ない」と誤解させるため1で下支えする
    total *= Math.max(count, 1);
  }
  return total;
}
